import React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { DashboardIcon } from '../../components/ui/DashboardIcon';
import { Role, User } from '../../types/app';

type ProfileProps = {
  role: Role | '';
  user: User;
};

export function Profile({ role, user }: ProfileProps) {
  const navigate = useNavigate();
  const roleLabel = formatLabel(role || 'user');
  const kycStatus = user.kycStatus ?? user.onboardingStatus;
  const emailStatus = user.emailVerified === false ? 'Not verified' : 'Verified';

  return (
    <div className="profile-page">
      <Card className="profile-hero">
        <CardContent>
          <div className="profile-identity">
            <Avatar className="profile-avatar">{user.initials || getInitials(user.name)}</Avatar>
            <div>
              <Typography component="h2" variant="h2">{user.name}</Typography>
              <Typography color="text.secondary">{user.email}</Typography>
              <Stack className="profile-chips" direction="row" spacing={1} useFlexGap>
                <Chip color="primary" label={roleLabel} size="small" />
                <Chip
                  color={user.emailVerified === false ? 'warning' : 'success'}
                  label={`Email ${emailStatus.toLowerCase()}`}
                  size="small"
                  variant="outlined"
                />
                {kycStatus ? (
                  <Chip
                    color={getStatusColor(kycStatus)}
                    label={`KYC ${formatLabel(kycStatus)}`}
                    size="small"
                    variant="outlined"
                  />
                ) : null}
              </Stack>
            </div>
          </div>

          <Button
            onClick={() => navigate('/account/change-password')}
            startIcon={<DashboardIcon type="lock" size={18} />}
            variant="contained"
          >
            Change password
          </Button>
        </CardContent>
      </Card>

      <div className="profile-grid">
        <Card className="profile-card">
          <CardContent>
            <div className="profile-card-heading">
              <span className="profile-card-icon" aria-hidden="true">
                <DashboardIcon type="user" size={22} />
              </span>
              <div>
                <Typography component="h3" variant="h3">Account details</Typography>
                <Typography color="text.secondary">Your identity and access information.</Typography>
              </div>
            </div>
            <Divider />
            <dl className="profile-details">
              <ProfileDetail label="Full name" value={user.name} />
              <ProfileDetail label="Email address" value={user.email} />
              <ProfileDetail label="Account role" value={roleLabel} />
              <ProfileDetail label="User ID" value={user.id} mono />
            </dl>
          </CardContent>
        </Card>

        <Card className="profile-card">
          <CardContent>
            <div className="profile-card-heading">
              <span className="profile-card-icon" aria-hidden="true">
                <DashboardIcon type="shield" size={22} />
              </span>
              <div>
                <Typography component="h3" variant="h3">Account status</Typography>
                <Typography color="text.secondary">Verification and onboarding readiness.</Typography>
              </div>
            </div>
            <Divider />
            <dl className="profile-details">
              <ProfileDetail label="Email verification" value={emailStatus} />
              <ProfileDetail label="KYC status" value={kycStatus ? formatLabel(kycStatus) : 'Not required'} />
              <ProfileDetail
                label="Onboarding"
                value={user.onboardingRequired ? 'Action required' : 'Complete'}
              />
              <ProfileDetail label="Session access" value="Active" />
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card className="profile-security">
        <CardContent>
          <div className="profile-card-heading">
            <span className="profile-card-icon" aria-hidden="true">
              <DashboardIcon type="lock" size={22} />
            </span>
            <div>
              <Typography component="h3" variant="h3">Security</Typography>
              <Typography color="text.secondary">
                Keep your operator account protected with a strong, unique password.
              </Typography>
            </div>
          </div>
          <Button onClick={() => navigate('/account/change-password')} variant="outlined">
            Update password
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileDetail({ label, mono = false, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? 'profile-mono' : undefined}>{value || 'Not available'}</dd>
    </div>
  );
}

function formatLabel(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';
}

function getStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  const normalized = status.toLowerCase();
  if (['approved', 'completed', 'verified'].includes(normalized)) return 'success';
  if (['rejected', 'failed'].includes(normalized)) return 'error';
  if (['pending', 'submitted', 'in_review'].includes(normalized)) return 'warning';
  return 'default';
}
